import { useCallback, useEffect } from 'react';

import { DateInput } from '@/_components';
import { snackbar } from '@/_shared';

import { FilterTypeSelector } from '../filter-type-selector';
import { FilterApplyTrigger } from '../filters-apply-trigger';
import { getFilterKey, getFilterValue, useFiltersState } from '../helper';
import type { AdvanceFilter } from '../types';

export function DateFilterWithRadios<T>({
  handleSubmitAdvFilters,
  ...perFilter
}: AdvanceFilter<T> & {
  handleSubmitAdvFilters: (v: AdvanceFilter<T>) => void;
}) {
  const { mainKey, subKey, subKey2, types, selectedType, filterValue, applied } = perFilter;
  const {
    fromState: [fromValue, setFromValue],
    toState: [toValue, setToValue],
    typeState: [type, setType],
    showTwoValues,
    inputValueRequired,
    transition: [isPending, startTransition],
  } = useFiltersState(filterValue ?? '', types, selectedType);

  const handleSubmit = useCallback(() => {
    if (fromValue.length === 0 && inputValueRequired) {
      snackbar.error('Please enter the value to search');
      return;
    }

    if (toValue.length === 0 && showTwoValues && inputValueRequired) {
      snackbar.error('Please enter the second value to search');
      return;
    }

    if (type === null || typeof type === 'undefined' || type === '') {
      snackbar.error('Please select a filter type');
      return;
    }

    const selectedType = types.find((f) => f.value === type) ?? { name: '', value: '' };
    const key = getFilterKey(mainKey as string, subKey, subKey2, selectedType?.absoluteKey);
    const { filter, filterValue } = getFilterValue(type, key, showTwoValues ? [fromValue, toValue] : [fromValue]);

    startTransition(() => {
      handleSubmitAdvFilters({
        ...perFilter,
        applied: true,
        filterValue,
        valueToApply: filter,
        selectedType,
      });
    });
  }, [
    fromValue,
    inputValueRequired,
    toValue,
    showTwoValues,
    type,
    mainKey,
    subKey,
    subKey2,
    startTransition,
    handleSubmitAdvFilters,
    perFilter,
    types,
  ]);

  useEffect(() => {
    if (!applied) {
      setFromValue('');
      setToValue('');
      setType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  return (
    <section className="flex flex-col">
      {inputValueRequired && (
        <div className="flex gap-1">
          <div className="w-full">
            <DateInput
              value={fromValue === '' ? undefined : fromValue}
              maxDate={showTwoValues ? new Date(toValue) : undefined}
              popperPlacement="bottom-start"
              onValueChange={(date) => setFromValue(date ?? '')}
              renderInPortal={false}
            />
          </div>
          {showTwoValues && (
            <div className="w-full">
              <DateInput
                value={toValue === '' ? undefined : toValue}
                minDate={showTwoValues ? new Date(fromValue) : undefined}
                popperPlacement="bottom-start"
                onValueChange={(date) => setToValue(date ?? '')}
                renderInPortal={false}
              />
            </div>
          )}
        </div>
      )}

      <FilterTypeSelector types={types} type={type} setType={setType} />
      <FilterApplyTrigger handleSubmit={handleSubmit} isPending={isPending} />
    </section>
  );
}
